import { normalizeUnitOfMeasure, parseQtyInput, quantityAllowsFraction } from '@shared/quantity';
import type { Product } from '../../catalog/domain-api';
import type { PhysicalCountMixedItem, StockRow, VariantStockCellRow } from '../stock/public-api';
import type { PhysicalCountRow } from './inventoryOperations.types';

type CatalogProduct = Pick<Product, 'id' | 'name' | 'sku' | 'unitOfMeasure' | 'imageUrl'>;

export type BuildPhysicalCountRowsOptions = {
  /** Gate de capability `product_variants`. Si false, se ignora `variantCellsByProductId`. */
  variantsEnabled?: boolean;
  /** Celdas de variante por productId (parent). Solo se usan si `variantsEnabled` es true. */
  variantCellsByProductId?: Map<string, VariantStockCellRow[]>;
};

export function buildPhysicalCountRows(
  stockRows: StockRow[],
  catalogProducts: CatalogProduct[],
  options: BuildPhysicalCountRowsOptions = {}
): PhysicalCountRow[] {
  const { variantsEnabled = false, variantCellsByProductId } = options;
  const stockByProductId = new Map(stockRows.map((row) => [row.productId, row]));
  const seenProductIds = new Set<string>();
  const merged: PhysicalCountRow[] = [];

  for (const product of catalogProducts) {
    seenProductIds.add(product.id);
    const stock = stockByProductId.get(product.id);
    const variantCells =
      variantsEnabled && variantCellsByProductId
        ? (variantCellsByProductId.get(product.id) ?? [])
        : [];
    const hasVariantCells = variantCells.length > 0;

    if (hasVariantCells) {
      // Productos con variantes: la fila parent solo se incluye si existe
      // una fila de stock con variant_id IS NULL (stockRows ya filtra parent).
      if (stock) {
        merged.push({
          id: stock.id,
          productId: product.id,
          stockId: stock.id,
          productName: stock.productName,
          productSku: stock.productSku,
          systemQty: stock.quantity,
          countedQty: String(stock.quantity),
          unitOfMeasure: normalizeUnitOfMeasure(stock.unitOfMeasure ?? product.unitOfMeasure),
          variantId: null,
          variantLabel: null,
          imageUrl: stock.imageUrl ?? product.imageUrl ?? null,
        });
      }
      for (const cell of variantCells) {
        merged.push({
          id: cell.stockId ?? cell.variantId,
          productId: product.id,
          stockId: cell.stockId,
          productName: product.name,
          productSku: product.sku ?? null,
          systemQty: cell.quantity,
          countedQty: String(cell.quantity),
          unitOfMeasure: normalizeUnitOfMeasure(product.unitOfMeasure),
          variantId: cell.variantId,
          variantLabel: cell.label,
          imageUrl: cell.imageUrl ?? product.imageUrl ?? null,
        });
      }
    } else if (stock) {
      merged.push({
        id: stock.id,
        productId: product.id,
        stockId: stock.id,
        productName: stock.productName,
        productSku: stock.productSku,
        systemQty: stock.quantity,
        countedQty: String(stock.quantity),
        unitOfMeasure: normalizeUnitOfMeasure(stock.unitOfMeasure ?? product.unitOfMeasure),
        imageUrl: stock.imageUrl ?? product.imageUrl ?? null,
      });
    } else {
      merged.push({
        id: `catalog:${product.id}`,
        productId: product.id,
        stockId: null,
        productName: product.name,
        productSku: product.sku ?? null,
        systemQty: 0,
        countedQty: '0',
        unitOfMeasure: normalizeUnitOfMeasure(product.unitOfMeasure),
        imageUrl: product.imageUrl ?? null,
      });
    }
  }

  for (const stock of stockRows) {
    if (seenProductIds.has(stock.productId)) continue;
    merged.push({
      id: stock.id,
      productId: stock.productId,
      stockId: stock.id,
      productName: stock.productName,
      productSku: stock.productSku,
      systemQty: stock.quantity,
      countedQty: String(stock.quantity),
      unitOfMeasure: normalizeUnitOfMeasure(stock.unitOfMeasure),
      imageUrl: stock.imageUrl ?? null,
    });
  }

  merged.sort((a, b) => a.productName.localeCompare(b.productName, 'es'));
  return merged;
}

export function diffPhysicalCountRows(rows: PhysicalCountRow[]): PhysicalCountMixedItem[] {
  const toApply = rows.filter((row) => {
    const counted = parseQtyInput(row.countedQty, row.unitOfMeasure);
    return counted !== null && counted !== row.systemQty;
  });

  const mixedItems: PhysicalCountMixedItem[] = [];
  for (const row of toApply) {
    const counted = parseQtyInput(row.countedQty, row.unitOfMeasure)!;
    if (row.stockId) {
      mixedItems.push({
        kind: 'adjust',
        stockId: row.stockId,
        delta: counted - row.systemQty,
        variantId: row.variantId ?? null,
      });
    } else if (counted > 0) {
      mixedItems.push({
        kind: 'receive',
        product: { id: row.productId, name: row.productName },
        quantity: counted,
        unitCost: 0,
      });
    }
  }
  return mixedItems;
}

export function countPhysicalCountDiffs(rows: PhysicalCountRow[]): number {
  return diffPhysicalCountRows(rows).length;
}

/**
 * Filas con un valor cargado que no se puede interpretar (parse → null).
 * Se descartan al aplicar — el UI las marca y avisa cuántas quedaron afuera.
 */
export function countPhysicalCountInvalidRows(rows: PhysicalCountRow[]): number {
  return rows.filter(isPhysicalCountRowInvalid).length;
}

/** True si la fila tiene un valor cargado que no se puede interpretar. */
export function isPhysicalCountRowInvalid(row: Pick<PhysicalCountRow, 'countedQty' | 'unitOfMeasure'>): boolean {
  return row.countedQty.trim() !== '' && parseQtyInput(row.countedQty, row.unitOfMeasure) === null;
}

/**
 * Filas de productos sin fila de stock previa que el conteo va a crear
 * (receive con costo $0 — el UI pide confirmación explícita).
 */
export function countPhysicalCountNewStockRows(rows: PhysicalCountRow[]): number {
  return rows.filter((row) => {
    if (row.stockId != null) return false;
    const counted = parseQtyInput(row.countedQty, row.unitOfMeasure);
    return counted !== null && counted > 0;
  }).length;
}

/**
 * True cuando el usuario tipea un separador decimal en un producto que se
 * cuenta en unidades enteras. El input lo sanitiza a entero (12,5 → 125):
 * el hint inline avisa antes de que pase desapercibido.
 */
export function isIntegerUnitDecimalAttempt(
  rawInput: string,
  uom?: PhysicalCountRow['unitOfMeasure'] | null
): boolean {
  return !quantityAllowsFraction(uom) && /[.,]/.test(rawInput);
}
